import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import {
  getCommentLabelTextString,
  getDaysOfWeekStrings,
} from 'store/strings/selectors'

import type { Props } from 'services/content/components/types/DailyModule'
import {
  generateCommentsFullPathUrl,
  parseDateString,
  formatDate,
  dayOfWeek,
} from 'ui-component-library/utils/helpers'
import DailyModule from 'ui-component-library/components/DailyModule'
import FeaturedContent from 'ui-component-library/components/DailyModule/FeaturedContent'
import WorkoutOfTheDay from './WorkoutOfTheDay'
import useCommentsCount from 'hooks/useCommentsCount'
import DateNavigation from 'ui-component-library/components/DailyModule/DateNavigation'

type AdditionalProps = Props & {
  fullPath: string
  onPreviousClick?: () => void
  onNextClick?: () => void
}

function DailyModuleContainer({
  workoutOfTheDay,
  fullPath,
  featuredContent,
  slug,
  onNextClick,
  onPreviousClick,
  nextUrl,
  previousUrl,
  previousDayLabelText,
  nextDayLabelText,
}: AdditionalProps) {
  const location = useLocation()
  const commentLabelText = useSelector(getCommentLabelTextString)
  const daysOfWeek = useSelector(getDaysOfWeekStrings)
  const { lessText, moreText } = workoutOfTheDay
  const featuredContentCommentsCount = useCommentsCount(
    featuredContent ? featuredContent.topicId : undefined,
  )

  const commentsUrl = useMemo(() => {
    if (!featuredContent) {
      return undefined
    }
    const { topicId } = featuredContent

    if (!topicId) {
      return undefined
    }

    let path = ''

    // fullPath only exists on dailyPage
    if (fullPath) {
      path = fullPath
    }

    // if on wodPage use article url
    if (featuredContent.type === 'image') {
      const { imageContent } = featuredContent

      if (imageContent && imageContent.button && imageContent.button.url) {
        path = imageContent.button.url
      }
    }

    return generateCommentsFullPathUrl({ fullPath: path, topicId })
  }, [featuredContent, fullPath])

  const currentDate = parseDateString(slug)

  const dateText = formatDate(currentDate, 'yyMMdd')
  const dayText = daysOfWeek[dayOfWeek(currentDate)]

  return (
    <DailyModule
      lessText={lessText}
      moreText={moreText}
      dateNavigation={
        <DateNavigation
          dayText={dayText}
          dateText={dateText}
          previousUrl={previousUrl}
          previousDayLabelText={previousDayLabelText}
          onPreviousClick={onPreviousClick}
          nextUrl={nextUrl}
          nextDayLabelText={nextDayLabelText}
          onNextClick={onNextClick}
          url={location.pathname === fullPath ? undefined : fullPath}
        />
      }
      workoutOfTheDay={
        <WorkoutOfTheDay
          {...workoutOfTheDay}
          fullPath={fullPath}
          date={currentDate}
          commentLabelText={commentLabelText}
        />
      }
      featuredContent={
        featuredContent
          ? ({ onLoaded }) => (
              <FeaturedContent
                {...featuredContent}
                commentLabelText={commentLabelText}
                commentsUrl={commentsUrl}
                commentsCount={featuredContentCommentsCount}
                onLoaded={onLoaded}
              />
            )
          : undefined
      }
    />
  )
}

export default DailyModuleContainer
